import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './location-viewer.style.js';

import { updateLocation, getLocationsByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';

@customElement('location-viewer')
export class LocationViewer extends LitElement {
  @property({ type: Boolean })
  open = false;

  @property({ type: Object })
  location: any = null;

  @property({ type: Object }) firestore: Firestore = null as any;

  @state()
  private isEditing = false;

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private originalLocation: any = null;


  static styles = styles;

  private normalizeLocationName(name: string): string {
    return name
      .trim() // Remove leading/trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .toLowerCase(); // Convert to lowercase
  }

  private async checkDuplicateLocationName(name: string, excludeLocationId?: string): Promise<{ isDuplicate: boolean; existingName?: string }> {
    try {
      // Normalize the input name for comparison
      const normalizedInputName = this.normalizeLocationName(name);
      
      // Use the existing API function to get locations by company
      const result = await getLocationsByCompany(this.firestore, this.location.company_id);
      
      if (result.error) {
        console.error('Error fetching locations for duplicate check:', result.error);
        return { isDuplicate: false }; // Allow submission if we can't check
      }
      
      if (result.data && result.data.length > 0) {
        // Check if any existing location has the same normalized name (excluding current location)
        for (const location of result.data) {
          const existingName = location.model?.name;
          if (existingName && location.id !== excludeLocationId) {
            const normalizedExistingName = this.normalizeLocationName(existingName);
            if (normalizedExistingName === normalizedInputName) {
              return { isDuplicate: true, existingName: existingName };
            }
          }
        }
      }
      
      return { isDuplicate: false }; // No existing locations, so no duplicates
    } catch (error) {
      console.error('Error checking for duplicate location name:', error);
      // If we can't check for duplicates, allow the submission to proceed
      return { isDuplicate: false };
    }
  }

  private async handleSave() {
    if (!this.location) return;

    // Check for duplicate location name (excluding current location)
    const duplicateCheck = await this.checkDuplicateLocationName(this.location.name, this.location.id);
    if (duplicateCheck.isDuplicate) {
      const normalizedExistingName = this.normalizeLocationName(duplicateCheck.existingName!);
      this.errorMessage = `A building with the name '${normalizedExistingName}' already exists`;
      return;
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      // Normalize the name before saving
      const normalizedName = this.normalizeLocationName(this.location.name);

      const updateData = {
        ...this.location,
        name: normalizedName,
        updated_at: Date.now()
      };

      const result = await updateLocation(this.firestore, this.location.id, updateData);
      
      if (!result.error) {
        // Update successful
        this.location = { ...this.location, name: normalizedName };
        this.isEditing = false;
        this.originalLocation = null;
        
        // Dispatch event to notify parent component
        this.dispatchEvent(new CustomEvent('location-updated', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Failed to update location';
      }
    } catch (error: any) {
      console.error('Error updating location:', error);
      this.errorMessage = error.message || 'Failed to update location';
    } finally {
      this.isLoading = false;
    }
  }

  private handleEdit() {
    this.originalLocation = { ...this.location };
    this.isEditing = true;
    this.errorMessage = '';
  }

  private handleCancel() {
    if (this.originalLocation) {
      this.location = { ...this.originalLocation };
    }
    this.isEditing = false;
    this.originalLocation = null;
    this.errorMessage = '';
  }

  private handleFieldUpdate(field: string, value: any) {
    if (this.location) {
      this.location[field] = value;
    }
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (!this.location) {
      return html``;
    }

    const locationName = this.location.name;
    const locationType = this.location.location_type;

    return html`
      <sl-dialog
        label="Location Viewer"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="location-header">
          <h2>${locationName}</h2>
          <div class="location-type-badge">
            <sl-badge variant="primary">${locationType}</sl-badge>
          </div>
        </div>

        ${this.errorMessage ? html`
          <div class="error-message">
            <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
            ${this.errorMessage}
          </div>
        ` : ''}

        <div class="location-fields">

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Name:</div>
            <div class="field-value">
              <sl-input
                value=${this.location.name}
                @sl-input=${(e: Event) => this.handleFieldUpdate('name', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Type:</div>
            <div class="field-value">
              <sl-select
                value=${this.location.location_type || ''}
                @sl-change=${(e: Event) => this.handleFieldUpdate('location_type', (e.target as HTMLSelectElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                ?disabled=${!this.isEditing}
              >
                <sl-option value="Office">Office</sl-option>
                <sl-option value="Warehouse">Warehouse</sl-option>
                <sl-option value="Production">Production</sl-option>
                <sl-option value="Quality_Control">Quality Control</sl-option>
                <sl-option value="Storage">Storage</sl-option>
                <sl-option value="Shipping">Shipping</sl-option>
                <sl-option value="Receiving">Receiving</sl-option>
                <sl-option value="Other">Other</sl-option>
              </sl-select>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Status:</div>
            <div class="field-value">
              <sl-select
                value=${this.location.is_active ? 'active' : 'inactive'}
                @sl-change=${(e: Event) => this.handleFieldUpdate('is_active', (e.target as HTMLSelectElement).value === 'active')}
                @click=${(e: Event) => e.stopPropagation()}
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                ?disabled=${!this.isEditing}
              >
                <sl-option value="active">Active</sl-option>
                <sl-option value="inactive">Inactive</sl-option>
              </sl-select>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Address:</div>
            <div class="field-value">
              <sl-input
                value=${this.location.address || ''}
                @sl-input=${(e: Event) => this.handleFieldUpdate('address', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                placeholder="Enter address"
                size="large"
                ?disabled=${!this.isEditing}
              >
                <sl-icon name="geo-alt" slot="prefix"></sl-icon>
              </sl-input>
            </div>
          </div>

        <div slot="footer" class="dialog-footer">
          ${this.isEditing ? html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleCancel}
              ?disabled=${this.isLoading}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleSave}
              ?loading=${this.isLoading}
              ?disabled=${this.isLoading}
            >
              ${this.isLoading ? 'Saving...' : 'Save'}
            </sl-button>
          ` : html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleEdit}
            >
              Edit
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.closeModal}
            >
              Done
          </sl-button>
          `}
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'location-viewer': LocationViewer;
  }
}
